// @ts-nocheck
/**
 * バトル管理フック
 * @module twinKey/hooks/useBattle
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useTwinKeyContext } from '../context/TwinKeyContext';
import { BattleEngine, BATTLE_STATUS, partyToBattleUnits, createEnemyUnit, applyPartyBoosts } from '../core';

// ============================================================
// バトル設定の永続化
// ============================================================

const BATTLE_SETTINGS_KEY = 'twinKey_battleSettings';

/**
 * バトル設定を読み込む
 */
function loadBattleSettings() {
  try {
    const saved = localStorage.getItem(BATTLE_SETTINGS_KEY);
    if (saved) {
      const settings = JSON.parse(saved);
      return {
        isAuto: settings.isAuto ?? true,
        speed: settings.speed ?? 1,
      };
    }
  } catch (e) {
    console.warn('バトル設定の読み込みに失敗:', e);
  }
  return { isAuto: true, speed: 1 };
}

/**
 * バトル設定を保存
 */
function saveBattleSettings(settings) {
  try {
    localStorage.setItem(BATTLE_SETTINGS_KEY, JSON.stringify(settings));
  } catch (e) {
    console.warn('バトル設定の保存に失敗:', e);
  }
}

/**
 * バトル管理フック
 * @returns {Object} バトル操作API
 */
export function useBattle() {
  const {
    state,
    actions,
    allCards,
  } = useTwinKeyContext();

  // バトルエンジン参照
  const engineRef = useRef(null);

  // 保存された設定を読み込む
  const savedSettings = useRef(loadBattleSettings());

  // バトル状態
  const [battleState, setBattleState] = useState(null);
  // 行動待ち状態
  const [waitingForInput, setWaitingForInput] = useState(false);
  // 選択中のスキル
  const [selectedSkill, setSelectedSkill] = useState(null);
  // 選択中のターゲット
  const [selectedTarget, setSelectedTarget] = useState(null);
  // オートモード（保存された値で初期化）
  const [isAuto, setIsAuto] = useState(savedSettings.current.isAuto);
  // 速度（保存された値で初期化）
  const [speed, setSpeed] = useState(savedSettings.current.speed);
  // アニメーション中
  const [isAnimating, setIsAnimating] = useState(false);
  // 最後のアクション結果（演出用）
  const [lastActionResult, setLastActionResult] = useState(null);

  // ============================================================
  // バトル初期化
  // ============================================================

  /**
   * コンテキストの currentBattle が設定されたら自動的にバトルを初期化
   */
  useEffect(() => {
    // currentBattle が設定されていて、まだ battleState が null の場合に初期化
    if (state.currentBattle && !battleState && state.activeParty) {
      const party = state.activeParty;
      const battleInfo = state.currentBattle;

      // 味方ユニット作成（装備効果適用済み）
      const rawAllies = partyToBattleUnits(party);
      // パーティ全体へのブースト適用（category_boost, faction_boost）
      const allies = applyPartyBoosts(rawAllies);

      // 敵ユニット作成
      const enemies = battleInfo.enemies?.map((enemyData, index) => {
        // カードデータから敵を生成
        const cardData = allCards?.find(c => c.id === enemyData.cardId);
        if (cardData) {
          return createEnemyUnit(cardData, enemyData.level, index);
        }

        // プレースホルダー敵（カードデータなし）
        return {
          id: `enemy_${index}`,
          name: enemyData.name || '敵',
          isAlly: false,
          attribute: enemyData.attribute || 'なし',
          stats: {
            attack: enemyData.level * 50 + 100,
            maxHp: enemyData.level * 100 + 200,
            currentHp: enemyData.level * 100 + 200,
            defense: enemyData.level * 2,
            speed: 50 + Math.floor(Math.random() * 30),
          },
          skills: [],
          isAlive: true,
          isDefending: false,
        };
      }) || [];

      // 敵がいない場合は初期化しない
      if (enemies.length === 0) {
        console.warn('敵ユニットが生成できませんでした', battleInfo);
        return;
      }

      // エンジン作成
      const engine = new BattleEngine(allies, enemies, {
        floorNumber: battleInfo.dungeonFloor,
        battleType: battleInfo.battleType,
        isAuto,
        speed,
        onChange: (newState) => setBattleState(newState),
      });

      engineRef.current = engine;
      setBattleState(engine.getState());
      setWaitingForInput(false);
    }
  }, [state.currentBattle, state.activeParty, battleState, allCards, isAuto, speed]);

  /**
   * バトルを開始
   * @param {Object} party - パーティ
   * @param {Object} battleInfo - バトル情報
   */
  const startBattle = useCallback((party, battleInfo) => {
    if (!party || !battleInfo) return;

    // 味方ユニット作成（装備効果適用済み）
    const rawAllies = partyToBattleUnits(party);
    // パーティ全体へのブースト適用（category_boost, faction_boost）
    const allies = applyPartyBoosts(rawAllies);

    // 敵ユニット作成
    const enemies = battleInfo.enemies.map((enemyData, index) => {
      // カードデータから敵を生成
      const cardData = allCards?.find(c => c.id === enemyData.cardId);
      if (cardData) {
        return createEnemyUnit(cardData, enemyData.level, index);
      }

      // プレースホルダー敵（カードデータなし）
      return {
        id: `enemy_${index}`,
        name: enemyData.name || '敵',
        isAlly: false,
        attribute: enemyData.attribute || 'なし',
        stats: {
          attack: enemyData.level * 50 + 100,
          maxHp: enemyData.level * 100 + 200,
          currentHp: enemyData.level * 100 + 200,
          defense: enemyData.level * 2,
          speed: 50 + Math.floor(Math.random() * 30),
        },
        skills: [],
        isAlive: true,
        isDefending: false,
      };
    });

    // エンジン作成
    const engine = new BattleEngine(allies, enemies, {
      floorNumber: battleInfo.dungeonFloor,
      battleType: battleInfo.battleType,
      isAuto,
      speed,
      onChange: (newState) => setBattleState(newState),
    });

    engineRef.current = engine;
    setBattleState(engine.getState());
    setWaitingForInput(false);

    // バトル開始通知
    actions.startBattle(engine.getState());
  }, [allCards, isAuto, speed, actions]);

  // ============================================================
  // バトル実行
  // ============================================================

  /**
   * 次の行動を実行
   */
  const executeNextAction = useCallback(() => {
    if (!engineRef.current || isAnimating) return;

    // 行動前の状態を取得（行動者情報を取得するため）
    const prevState = engineRef.current.getState();
    const currentUnitId = prevState.turnOrder[prevState.currentTurnIndex];
    const allUnits = [...prevState.allies, ...prevState.enemies];
    const actor = allUnits.find(u => u.id === currentUnitId);

    const result = engineRef.current.executeNextAction();
    const newState = engineRef.current.getState();

    // 演出用のアクション結果を作成
    if (result.actionResult && actor) {
      const actionResult = result.actionResult;
      const targetIds = actionResult.results?.map(r => r.targetId) || [];
      const targets = allUnits.filter(u => targetIds.includes(u.id));

      setLastActionResult({
        ...actionResult,
        actor: {
          id: actor.id,
          name: actor.name,
          attribute: actor.attribute,
          isAlly: actor.isAlly,
        },
        targets: targets.map(t => ({
          id: t.id,
          name: t.name,
          attribute: t.attribute,
          isAlly: t.isAlly,
        })),
        timestamp: Date.now(),
      });

      // 演出時間後にクリア（1倍速は長めに）
      const clearDelay = [2000, 800, 500][speed - 1] || 800;
      setTimeout(() => {
        setLastActionResult(null);
      }, clearDelay);
    }

    setBattleState(newState);

    if (result.waitingForInput) {
      setWaitingForInput(true);
    } else if (result.done && engineRef.current) {
      // バトル終了
      const battleResult = engineRef.current.getBattleResult();
      actions.endBattle(battleResult);
    }

    return result;
  }, [isAnimating, actions, speed]);

  /**
   * 手動行動を実行
   * @param {Object} action - 行動 { type, skillId?, targetId }
   */
  const executeManualAction = useCallback((action) => {
    if (!engineRef.current) return;

    // 行動前の状態を取得（行動者情報を取得するため）
    const prevState = engineRef.current.getState();
    const currentUnitId = prevState.turnOrder[prevState.currentTurnIndex];
    const allUnits = [...prevState.allies, ...prevState.enemies];
    const actor = allUnits.find(u => u.id === currentUnitId);

    setWaitingForInput(false);
    setSelectedSkill(null);
    setSelectedTarget(null);

    // アニメーション
    setIsAnimating(true);

    const result = engineRef.current.executeManualAction(action);
    const newState = engineRef.current.getState();

    // 演出用のアクション結果を作成（actionResultを正しく参照）
    if (result.actionResult && actor) {
      const actionResult = result.actionResult;
      const targetIds = actionResult.results?.map(r => r.targetId) || [];
      const targets = allUnits.filter(u => targetIds.includes(u.id));

      setLastActionResult({
        ...actionResult,
        actor: {
          id: actor.id,
          name: actor.name,
          attribute: actor.attribute,
          isAlly: actor.isAlly,
        },
        targets: targets.map(t => ({
          id: t.id,
          name: t.name,
          attribute: t.attribute,
          isAlly: t.isAlly,
        })),
        timestamp: Date.now(),
      });
    }

    setBattleState(newState);

    // 演出時間後にクリアして次へ（1倍速は長めに）
    const clearDelay = [2000, 800, 500][speed - 1] || 800;
    setTimeout(() => {
      setLastActionResult(null);
      setIsAnimating(false);

      if (result.done && engineRef.current) {
        const battleResult = engineRef.current.getBattleResult();
        actions.endBattle(battleResult);
      }
    }, clearDelay);
  }, [actions, speed]);

  /**
   * 攻撃を実行
   * @param {string} targetId - ターゲットID
   */
  const attack = useCallback((targetId) => {
    executeManualAction({ type: 'attack', targetId });
  }, [executeManualAction]);

  /**
   * スキルを使用
   * @param {string} skillId - スキルID
   * @param {string} targetId - ターゲットID
   */
  const useSkill = useCallback((skillId, targetId) => {
    executeManualAction({ type: 'skill', skillId, targetId });
  }, [executeManualAction]);

  /**
   * 防御
   */
  const defend = useCallback(() => {
    executeManualAction({ type: 'defend' });
  }, [executeManualAction]);

  // ============================================================
  // オートバトル
  // ============================================================

  /**
   * オートモード切り替え
   */
  const toggleAuto = useCallback(() => {
    const newAuto = !isAuto;
    setIsAuto(newAuto);
    engineRef.current?.setAutoMode(newAuto);
    // 設定を保存
    saveBattleSettings({ isAuto: newAuto, speed });
  }, [isAuto, speed]);

  /**
   * 速度変更
   * @param {number} newSpeed - 速度（1-3）
   */
  const changeSpeed = useCallback((newSpeed) => {
    setSpeed(newSpeed);
    engineRef.current?.setSpeed(newSpeed);
    // 設定を保存
    saveBattleSettings({ isAuto, speed: newSpeed });
  }, [isAuto]);

  /**
   * バトル実行ループ
   * オートモード: 常に自動進行
   * 手動モード: 味方の入力待ち以外は自動進行（敵ターンなど）
   */
  useEffect(() => {
    if (!battleState || battleState.status !== BATTLE_STATUS.ONGOING) {
      return;
    }

    // 入力待ちまたはアニメーション中は進行しない
    if (waitingForInput || isAnimating) {
      return;
    }

    // オートモードと手動モードで速度を変える
    // 手動モードでは敵ターンなどを素早く処理（演出は維持）
    const delay = isAuto
      ? [1000, 500, 200][speed - 1] || 500
      : [600, 300, 150][speed - 1] || 300; // 手動モードは少し速め

    const timer = setTimeout(() => {
      executeNextAction();
    }, delay);

    return () => clearTimeout(timer);
  }, [isAuto, battleState, waitingForInput, isAnimating, speed, executeNextAction]);

  // ============================================================
  // 逃走
  // ============================================================

  /**
   * 逃走を試みる
   */
  const escape = useCallback(() => {
    if (!engineRef.current) return { success: false };

    const result = engineRef.current.escape();
    setBattleState(engineRef.current.getState());

    if (result.success) {
      actions.endBattle(engineRef.current.getBattleResult());
    }

    return result;
  }, [actions]);

  // ============================================================
  // ヘルパー
  // ============================================================

  /**
   * 現在行動中のユニットを取得
   */
  const currentUnit = battleState
    ? [...battleState.allies, ...battleState.enemies].find(
      u => u.id === battleState.turnOrder[battleState.currentTurnIndex]
    )
    : null;

  /**
   * 有効なターゲットを取得
   */
  const validTargets = battleState && currentUnit
    ? (currentUnit.isAlly ? battleState.enemies : battleState.allies).filter(u => u.isAlive)
    : [];

  /**
   * バトル状態をリセット
   */
  const resetBattle = useCallback(() => {
    setBattleState(null);
    engineRef.current = null;
    setWaitingForInput(false);
    setSelectedSkill(null);
    setSelectedTarget(null);
  }, []);

  // ============================================================
  // 返却値
  // ============================================================

  return {
    // 状態
    battleState,
    currentUnit,
    validTargets,
    waitingForInput,
    selectedSkill,
    setSelectedSkill,
    selectedTarget,
    setSelectedTarget,
    isAuto,
    speed,
    isAnimating,
    lastActionResult, // 演出用アクション結果

    // バトル操作
    startBattle,
    executeNextAction,
    executeManualAction,
    attack,
    useSkill,
    defend,
    escape,
    resetBattle,

    // 設定
    toggleAuto,
    changeSpeed,
  };
}

export default useBattle;
